/**
 * MemberDetailsModal - Shows detailed information about a thread member or contact
 * WhatsApp-style member profile when clicking on an avatar in group chats
 */
'use client';

import React, { useState, useEffect } from 'react';
import { 
  X, Mail, Phone, Building2, Briefcase, Shield, Crown, 
  User, ChevronRight, MessageSquare, PhoneCall, Video,
  Calendar, MapPin, Bot, Code2
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { isThreadTypeJob, isThreadTypeSchedule } from '@/lib/messageSoundPreferences';

export interface MemberDetails {
  user_id: number;
  first_name?: string;
  last_name?: string;
  display_name: string;
  email?: string;
  phone?: string;
  avatar_url?: string;
  department?: string;
  job_title?: string;
  role?: string; // From module_access (owner, admin, supervisor, member)
  is_online?: boolean;
  last_activity_at?: string;
  // Integration message sender fields
  is_api_message?: boolean;
  api_key_owner_name?: string;
  api_key_owner_email?: string;
}

interface MemberDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: MemberDetails | null;
  threadType?: 'job' | 'schedule' | 'group' | 'client' | 'direct' | string;
  
  // Optional actions
  onStartVoiceCall?: (userId: number) => void;
  onStartVideoCall?: (userId: number) => void;
  onSendMessage?: (userId: number) => void;
  
  // Whether this is the current user (hide call/message buttons)
  isCurrentUser?: boolean;
  
  isDark?: boolean;
}

// Role badge colors and labels
const ROLE_CONFIG: Record<string, { label: string; bgColor: string; textColor: string; icon: React.ReactNode }> = {
  owner: {
    label: 'Owner',
    bgColor: 'bg-amber-500',
    textColor: 'text-white',
    icon: <Crown className="w-3 h-3" />
  },
  admin: {
    label: 'Admin',
    bgColor: 'bg-purple-500',
    textColor: 'text-white',
    icon: <Shield className="w-3 h-3" />
  },
  supervisor: {
    label: 'Supervisor',
    bgColor: 'bg-blue-500',
    textColor: 'text-white',
    icon: <Shield className="w-3 h-3" />
  }
};

export default function MemberDetailsModal({
  isOpen,
  onClose,
  member,
  threadType,
  onStartVoiceCall,
  onStartVideoCall,
  onSendMessage,
  isCurrentUser = false,
  isDark: isDarkProp
}: MemberDetailsModalProps) {
  const { currentTheme, clientBranding } = useTheme();
  const primaryColor = clientBranding?.primary_color || currentTheme?.colors?.primary || '#3b82f6';
  const isDark = isDarkProp ?? currentTheme?.mode === 'dark';
  
  // Extended member details from API
  const [extendedDetails, setExtendedDetails] = useState<MemberDetails | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Fetch extended member details when modal opens
  useEffect(() => {
    const fetchMemberDetails = async () => {
      if (!isOpen || !member?.user_id) return;
      
      setLoading(true);
      try {
        const tokens = authService.getTokens();
        if (!tokens?.access_token) return;
        
        // Pass thread_type to get role from correct module
        const moduleSlug = isThreadTypeJob(threadType) || isThreadTypeSchedule(threadType)
          ? 'total-operations' 
          : undefined;
        
        const url = new URL('/api/v1/messaging/member-details', window.location.origin);
        url.searchParams.set('user_id', String(member.user_id));
        if (moduleSlug) url.searchParams.set('module_slug', moduleSlug);
        
        const response = await fetch(url.toString(), {
          headers: {
            'Authorization': `Bearer ${tokens.access_token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.member) {
            setExtendedDetails(data.member);
          }
        }
      } catch (err) {
        console.error('[MemberDetailsModal] Error fetching member details:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMemberDetails();
  }, [isOpen, member?.user_id, threadType]);
  
  // Reset extended details when modal closes
  useEffect(() => {
    if (!isOpen) {
      setExtendedDetails(null);
    }
  }, [isOpen]);
  
  if (!isOpen || !member) return null;
  
  // Merge member prop with extended details (but NOT for API messages - don't overwrite with user details)
  const details: MemberDetails = member.is_api_message 
    ? member  // Keep API message info as-is
    : { ...member, ...extendedDetails };
  
  const fullName = details.first_name && details.last_name 
    ? `${details.first_name} ${details.last_name}`
    : details.display_name;
    
  const roleConfig = details.role && !details.is_api_message ? ROLE_CONFIG[details.role.toLowerCase()] : null;
  
  // Check if member is online (within 5 minutes) - not applicable for API messages
  const isOnline = !details.is_api_message && (details.is_online || (
    details.last_activity_at && 
    (Date.now() - new Date(details.last_activity_at).getTime()) < 5 * 60 * 1000
  ));
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className={`w-full max-w-sm rounded-2xl shadow-xl overflow-hidden ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header with avatar - branded color */}
        <div 
          className="relative pt-8 pb-4 px-6 text-center"
          style={{ backgroundColor: primaryColor }}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/20 text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Avatar */}
          <div className="relative inline-block">
            {details.avatar_url ? (
              <img 
                src={details.avatar_url}
                alt={fullName}
                className="w-24 h-24 rounded-full object-cover border-4 border-white/20 shadow-lg"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-white/20 border-4 border-white/20 flex items-center justify-center shadow-lg">
                <span className="text-3xl font-semibold text-white">
                  {fullName?.charAt(0)?.toUpperCase() || '?'}
                </span>
              </div>
            )}
            
            {/* Online indicator OR API badge */}
            {details.is_api_message ? (
              <div className="absolute bottom-1 right-1 w-6 h-6 rounded-full bg-purple-500 border-2 border-white flex items-center justify-center" title="API Integration">
                <Bot className="w-3.5 h-3.5 text-white" />
              </div>
            ) : (
              <div className={`absolute bottom-1 right-1 w-5 h-5 rounded-full border-3 border-white ${
                isOnline ? 'bg-green-500' : 'bg-gray-400'
              }`} />
            )}
          </div>
          
          {/* Name */}
          <h2 className="mt-3 text-xl font-semibold text-white">
            {fullName}
          </h2>
          
          {/* Role badge OR API Integration badge */}
          {details.is_api_message ? (
            <div className="inline-flex items-center gap-1 mt-2 px-3 py-1 rounded-full text-xs font-medium bg-purple-600 text-white">
              <Code2 className="w-3 h-3" />
              <span>Integration Sender</span>
            </div>
          ) : roleConfig && (
            <div className={`inline-flex items-center gap-1 mt-2 px-3 py-1 rounded-full text-xs font-medium ${roleConfig.bgColor} ${roleConfig.textColor}`}>
              {roleConfig.icon}
              <span>{roleConfig.label}</span>
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className={`p-4 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
          {loading && !details.is_api_message ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-3 rounded-full border-t-transparent animate-spin" style={{ borderColor: primaryColor }} />
            </div>
          ) : details.is_api_message ? (
            /* Integration message sender info */
            <>
              <div className={`mb-4 p-3 rounded-lg ${isDark ? 'bg-purple-900/30 border border-purple-700' : 'bg-purple-50 border border-purple-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Bot className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                  <p className={`text-sm font-medium ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>
                    Integration Generated Message
                  </p>
                </div>
                <p className={`text-xs ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                  This message was sent by an automation or integration identity rather than a signed-in user session.
                </p>
              </div>
              
              <div className="space-y-3">
                {/* Linked user */}
                {(details.api_key_owner_name || details.api_key_owner_email) && (
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isDark ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      <User className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    </div>
                    <div>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Linked User</p>
                      <p className="font-medium">{details.api_key_owner_name || 'Unknown'}</p>
                      {details.api_key_owner_email && (
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{details.api_key_owner_email}</p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Integration name (display_name) */}
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isDark ? 'bg-gray-700' : 'bg-gray-100'
                  }`}>
                    <Code2 className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Integration Name</p>
                    <p className="font-medium">{details.display_name}</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Contact info */}
              <div className="space-y-3">
                {/* Job Title */}
                {details.job_title && (
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isDark ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      <Briefcase className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    </div>
                    <div>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Job Title</p>
                      <p className="font-medium">{details.job_title}</p>
                    </div>
                  </div>
                )}
                
                {/* Department */}
                {details.department && (
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isDark ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      <Building2 className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    </div>
                    <div>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Department</p>
                      <p className="font-medium">{details.department}</p>
                    </div>
                  </div>
                )}
                
                {/* Email */}
                {details.email && (
                  <a 
                    href={`mailto:${details.email}`}
                    className={`flex items-center gap-3 p-2 -mx-2 rounded-lg transition-colors ${
                      isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isDark ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      <Mail className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Email</p>
                      <p className="font-medium truncate" style={{ color: primaryColor }}>{details.email}</p>
                    </div>
                    <ChevronRight className={`w-4 h-4 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                  </a>
                )}
                
                {/* Phone */}
                {details.phone && (
                  <a 
                    href={`tel:${details.phone}`}
                    className={`flex items-center gap-3 p-2 -mx-2 rounded-lg transition-colors ${
                      isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isDark ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      <Phone className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Phone</p>
                      <p className="font-medium" style={{ color: primaryColor }}>{details.phone}</p>
                    </div>
                    <ChevronRight className={`w-4 h-4 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                  </a>
                )}
                
                {/* No details available message */}
                {!details.job_title && !details.department && !details.email && !details.phone && (
                  <div className={`text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No additional details available</p>
                  </div>
                )}
              </div>
              
              {/* Action buttons - only show if not current user */}
              {!isCurrentUser && (onSendMessage || onStartVoiceCall || onStartVideoCall) && (
                <div className={`mt-6 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-center gap-4">
                    {onSendMessage && (
                      <button
                        onClick={() => onSendMessage(details.user_id)}
                        className={`flex flex-col items-center gap-1 p-3 rounded-xl transition-colors ${
                          isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                        }`}
                      >
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center text-white"
                          style={{ backgroundColor: primaryColor }}
                        >
                          <MessageSquare className="w-5 h-5" />
                        </div>
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Message</span>
                      </button>
                    )}
                    
                    {onStartVoiceCall && (
                      <button
                        onClick={() => onStartVoiceCall(details.user_id)}
                        className={`flex flex-col items-center gap-1 p-3 rounded-xl transition-colors ${
                          isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                        }`}
                      >
                        <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white">
                          <PhoneCall className="w-5 h-5" />
                        </div>
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Call</span>
                      </button>
                    )}
                    
                    {onStartVideoCall && (
                      <button
                        onClick={() => onStartVideoCall(details.user_id)}
                        className={`flex flex-col items-center gap-1 p-3 rounded-xl transition-colors ${
                          isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                        }`}
                      >
                        <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white">
                          <Video className="w-5 h-5" />
                        </div>
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Video</span>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
